import { useState, useEffect } from 'react';
import { X, Loader2, Plus, Hash, BellRing } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { SelectField } from '@/components/ui/SelectField';

interface LeadStatus { id: string; name: string; color: string; isActive?: boolean; }

interface Props {
  lead: { id: string; name: string; status?: string; hashtags?: string[] } | null;
  onClose: () => void;
  onSaved: () => void;
}

export function FollowupDrawer({ lead, onClose, onSaved }: Props) {
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [form, setForm] = useState({
    status: lead?.status ?? '',
    remark: '',
    nextType: 'none' as 'none' | 'schedule',
    nextFollowupAt: '',
  });
  const [hashtags, setHashtags] = useState<string[]>(lead?.hashtags ?? []);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/leads/statuses').then(({ data }) => setStatuses(data)).catch(() => {});
  }, []);

  const addTag = () => {
    const tag = newTag.trim().replace(/^#/, '');
    if (tag && !hashtags.includes(tag)) setHashtags(prev => [...prev, tag]);
    setNewTag('');
  };

  const handleSave = async () => {
    if (!form.status) { toast('error', 'Please select a status'); return; }
    if (!form.remark.trim()) { toast('error', 'Please enter a remark'); return; }
    if (form.nextType === 'schedule' && !form.nextFollowupAt) { toast('error', 'Please pick the next follow-up date & time'); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead!.id}/followups`, {
        status: form.status,
        remark: form.remark,
        hashtags,
        // Send as ISO (UTC) — the picker value is local time with no zone,
        // which the server would otherwise read in its own timezone.
        nextFollowupAt: form.nextType === 'schedule' && form.nextFollowupAt ? new Date(form.nextFollowupAt).toISOString() : undefined,
        // Scheduling a next follow-up always creates a reminder for the lead's
        // counselor (in-app + email + WhatsApp) at that time.
        createReminder: form.nextType === 'schedule' && !!form.nextFollowupAt,
      });
      toast('success', 'Followup added');
      onSaved();
      onClose();
    } catch {
      toast('error', 'Failed to add followup');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Add Followup</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Contact bar */}
        <div className="mx-6 mt-4 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium">
          Contact: {lead.name}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Followup Status */}
          <div>
            <h3 className="text-sm font-bold text-indigo-600 mb-3">Followup Details</h3>
            <label className="text-sm font-normal text-slate-700">Followup Status *</label>
            <SelectField
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Select Followup Status</option>
              {statuses.filter(s => s.isActive !== false).map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </SelectField>
          </div>

          {/* Remark */}
          <div>
            <label className="text-sm font-normal text-slate-700">Followup Remark *</label>
            <textarea
              value={form.remark}
              onChange={e => setForm(f => ({ ...f, remark: e.target.value.slice(0, 500) }))}
              placeholder="Enter followup details, conversation summary, or next steps..."
              rows={4}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-y"
            />
            <p className="text-xs text-slate-400 text-right mt-1">{form.remark.length}/500 characters</p>
          </div>

          {/* Next Followup */}
          <div>
            <h3 className="text-sm font-bold text-indigo-600 mb-3">Next Followup</h3>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                <input type="radio" name="nextType" checked={form.nextType === 'none'}
                  onChange={() => setForm(f => ({ ...f, nextType: 'none' }))}
                  className="mt-0.5 accent-indigo-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">No Followup</p>
                  <p className="text-xs text-slate-400">Mark this as final followup, no further action needed</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                <input type="radio" name="nextType" checked={form.nextType === 'schedule'}
                  onChange={() => setForm(f => ({ ...f, nextType: 'schedule' }))}
                  className="mt-0.5 accent-indigo-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Schedule Next Followup</p>
                  <p className="text-xs text-slate-400">Set a specific date and time for the next followup</p>
                </div>
              </label>

              {form.nextType === 'schedule' && (
                <div className="ml-8">
                  <label className="text-sm font-normal text-slate-700">Date & Time *</label>
                  <DateTimeInput
                    mode="datetime"
                    className="mt-1.5"
                    value={form.nextFollowupAt}
                    onChange={v => setForm(f => ({ ...f, nextFollowupAt: v }))}
                  />
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                    <BellRing className="h-3.5 w-3.5 text-[#6366F1]" />
                    A reminder is added automatically for this time.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1 mb-3">
              <Hash className="h-4 w-4" /> Hashtags
            </h3>
            <div className="flex gap-2 mb-2">
              <input
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add new hashtag (without #)"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              />
              <button onClick={addTag} className="p-2 bg-slate-200 hover:bg-slate-300 rounded-lg">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {hashtags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {hashtags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 text-xs px-2 py-1 rounded-md">
                    #{tag}
                    <button onClick={() => setHashtags(prev => prev.filter(t => t !== tag))} className="text-slate-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-emerald-600 italic">No hashtags added yet. Type above to add one.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3">
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.status || !form.remark.trim() || (form.nextType === 'schedule' && !form.nextFollowupAt)}
            className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="inline mr-2 h-4 w-4 animate-spin" />}
            Add Followup
          </button>
        </div>
      </div>
    </div>
  );
}
